import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  viewChild,
  ViewChild,
} from '@angular/core';
import { environment } from '../../../../environments/environment';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { AsyncPipe, CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import {
  catchError,
  filter,
  firstValueFrom,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';
import { LearningSectionContainerJsonModel } from '../learning-section-container.json.model';
import { MatDivider } from '@angular/material/divider';
import { MatInput } from '@angular/material/input';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatTab,
  MatTabChangeEvent,
  MatTabGroup,
  MatTabLabel,
} from '@angular/material/tabs';
import { AuthState } from '../../../core/authentication/auth.state';
import { ExplanationState } from '../../../explanation/ngxs/explanation/explanation.state';
import { ExplanationActions } from '../../../explanation/ngxs/explanation/explanation.actions';
import { FetchOptionsActions } from '../../../core/state/fetchOptions.actions';
import { CollectionFilter } from '../../../core/jsonApi/BaseCollectionService';
import { SequenceState } from '../../../sequence/ngxs/sequence/sequence.state';
import { SequenceActions } from '../../../sequence/ngxs/sequence/sequence.actions';
import { SequenceJsonModel } from '../../../sequence/sequence.json.model';
import { ExplanationJsonModel } from '../../../explanation/explanation.json.model';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { PixlEditorComponent } from '@wdidev/pixl-editor';
import { ExplanationContentJsonModel } from '../../../explanation/content/explanation-content.json.model';
import { LearningSectionContainerActions } from '../../ngxs/learning-section-container/learning-section-container.actions';
import { ExplanationContentActions } from '../../../explanation/ngxs/explanation-content/explanation-content.actions';
import { getLearningSectionContainerContentType } from '../../learning-section-container-content-type.layout.enum';
import { ExplanationContentState } from '../../../explanation/ngxs/explanation-content/explanation-content.state';
import { TestState } from '../../../test/ngxs/test/test.state';
import { TestJsonModel } from '../../../test/test.json.model';
import { TestActions } from '../../../test/ngxs/test/test.actions';
import { MatSnackBar } from '@angular/material/snack-bar';
import { map } from 'rxjs/operators';
import { LearningSectionContainerState } from '../../ngxs/learning-section-container/learning-section-container.state';
import {
  toggleContenteditable,
  toggleReadOnlyMathEditorInput,
} from '../../../core/util';

@Component({
  selector: 'pixl-learning-section-container-edit',
  imports: [
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatDialogModule,
    AsyncPipe,
    CommonModule,
    MatDivider,
    ReactiveFormsModule,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    MatError,
    MatInput,
    FormsModule,
    MatIconButton,
    MatCard,
    MatCardContent,
    MatSlideToggle,
    PixlEditorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './learning-section-container-single-edit.component.html',
  styleUrl: './learning-section-container-single-edit.component.scss',
})
export class LearningSectionContainerSingleEditComponent
implements OnInit, AfterViewInit {
  private snackBar = inject(MatSnackBar);

  private store = inject(Store);
  private fb = inject(FormBuilder);

  cdr = inject(ChangeDetectorRef);
  dialogRef = inject(MatDialogRef);

  public assetsUrl: string = environment.apiUrl + '/assets';
  public mediaUrl: string = environment.assetsHost + '/';

  public data = inject<LearningSectionContainerJsonModel>(MAT_DIALOG_DATA);

  auth$ = this.store.select(AuthState.getToken);
  public authToken: string | null = '';

  public formGroup = this.fb.group({
    title: [this.data.title, Validators.required],
    fullWidth: [this.data.fullWidth],
    content: this.data.content || null,
  });

  public currentPage = 1;

  explanationContents$ = this.store.select(
    ExplanationContentState.getExplanationContents,
  );

  @ViewChild('explanationContentSelect') explanationContentSelect!: MatSelect;

  explanations$ = this.store.select(ExplanationState.getExplanations);
  @ViewChild('explanationSelect') explanationSelect!: MatSelect;
  showExplanationTab = false;

  sequences$ = this.store.select(SequenceState.getSequences);
  @ViewChild('sequenceSelect') sequenceSelect!: MatSelect;

  tests$ = this.store.select(TestState.getTests);
  @ViewChild('testSelect') testSelect!: MatSelect;

  editor = viewChild(PixlEditorComponent);

  private currentOpenedSelect: MatSelect | null = null;
  public filters: CollectionFilter[] = [];
  public searchTerm = '';

  selectedContent:
    | SequenceJsonModel |
    ExplanationJsonModel |
    ExplanationContentJsonModel |
    TestJsonModel |
    null = null;

  public selectedTabLabel: string | null = null;
  public lockTabs = false;

  explanationContent$ = this.store.select(
    ExplanationContentState.getExplanationContent,
  );

  public explanationContentName = new FormControl('', [Validators.required]);
  public explanationContentHtml = '';
  private copyAndSaveExplanationContentHtml = ''; // Copy from explanationContentHtml for parse and save into DB

  public activeTabIndex = 0;

  ngOnInit() {
    this.auth$.pipe(take(1)).subscribe((token) => {
      this.authToken = token;
    });

    this.setActiveTab();

    this.resetFetch();

    if (this.data.content instanceof ExplanationContentJsonModel) {
      this.setEditorContent();
    }
  }

  ngAfterViewInit(): void {
    this.store.dispatch(
      new LearningSectionContainerActions.FetchOne(this.data.id),
    );
  }

  onTabChange(event: MatTabChangeEvent) {
    this.activeTabIndex = event.index;
    this.selectedTabLabel = event.tab.textLabel;
    this.resetFetch();
  }

  setActiveTab(): void {
    if (this.data?.content instanceof ExplanationContentJsonModel) {
      this.activeTabIndex = 0;
      this.selectedTabLabel = 'editor';
      this.lockTabs = true;
    }
    else if (this.data?.content instanceof SequenceJsonModel) {
      this.activeTabIndex = 1;
      this.selectedTabLabel = 'sequence';
      this.lockTabs = true;
    }
    else if (this.data?.content instanceof ExplanationJsonModel) {
      this.activeTabIndex = 2;
      this.selectedTabLabel = 'explanation';
      this.lockTabs = true;
    }
    else if (this.data?.content instanceof TestJsonModel) {
      this.activeTabIndex = 3;
      this.selectedTabLabel = 'test';
      this.lockTabs = true;
    }
    else {
      this.selectedTabLabel = 'editor';
      this.lockTabs = false;
    }
  }

  isTabLocked(tabLabel: string): boolean {
    return this.lockTabs && tabLabel !== this.selectedTabLabel;
  }

  resetFetch() {
    this.store.dispatch([
      new FetchOptionsActions.UpdateSortFieldName(''),
      new FetchOptionsActions.UpdateFilters([]),
    ]);

    if (this.selectedTabLabel === 'editor') {
      this.store.dispatch(
        new ExplanationContentActions.Fetch({
          pageNumber: 1,
          pageSize: 10,
          skipStoreDispatch: true,
        }),
      );
    }

    if (this.selectedTabLabel === 'explanation') {
      this.store.dispatch(
        new ExplanationActions.Fetch({
          pageNumber: 1,
          pageSize: 10,
          skipStoreDispatch: true,
        }),
      );
    }

    if (this.selectedTabLabel === 'sequence') {
      this.store.dispatch(
        new SequenceActions.Fetch({
          pageNumber: 1,
          pageSize: 10,
          skipStoreDispatch: true,
        }),
      );
    }

    if (this.selectedTabLabel === 'test') {
      this.store.dispatch(
        new TestActions.Fetch({
          pageNumber: 1,
          pageSize: 10,
          skipStoreDispatch: true,
        }),
      );
    }

    this.searchTerm = '';
    this.filters = [];
  }

  onSearchChange(event: FocusEvent) {
    if (!this.searchTerm) {
      this.resetFetch();
      return;
    }

    const existingFilterIndex = this.filters.findIndex(
      (f) => f.fieldName === 'nameLike',
    );

    if (existingFilterIndex !== -1) {
      this.filters[existingFilterIndex].value = this.searchTerm;
    }
    else {
      this.filters.push({ fieldName: 'nameLike', value: this.searchTerm });
    }

    this.store.dispatch([
      new FetchOptionsActions.UpdateFilters(this.filters),
      new FetchOptionsActions.ToDefault(),
    ]);

    const target = event.target as HTMLInputElement;

    if (target.name == 'explanationContentSearch') {
      this.store.dispatch(
        new ExplanationContentActions.Fetch({
          pageNumber: 1,
          pageSize: -1,
          skipStoreDispatch: true,
        }),
      );
    }

    if (target.name == 'explanationSearch') {
      this.store.dispatch(
        new ExplanationActions.Fetch({
          pageNumber: 1,
          pageSize: -1,
          skipStoreDispatch: true,
        }),
      );
    }

    if (target.name == 'sequenceSearch') {
      this.store.dispatch(
        new SequenceActions.Fetch({
          pageNumber: 1,
          pageSize: -1,
          skipStoreDispatch: true,
        }),
      );
    }

    if (target.name == 'testSearch') {
      this.store.dispatch(
        new TestActions.Fetch({
          pageNumber: 1,
          pageSize: -1,
          skipStoreDispatch: true,
        }),
      );
    }
  }

  onSelectOpened(opened: boolean, openedSelect: MatSelect) {
    if (opened) {
      this.currentPage = 1;
      this.currentOpenedSelect = openedSelect;

      setTimeout(() => {
        const panel = openedSelect.panel?.nativeElement;
        panel?.addEventListener('scroll', this.onScroll.bind(this));
      }, 0);
    }
    else {
      this.currentOpenedSelect = null;
    }
  }

  onScroll(event: Event) {
    if (!this.currentOpenedSelect) return;

    const target = event.target as HTMLElement;
    const selectName = this.currentOpenedSelect.placeholder || 'Unknown Select';

    if (target.scrollTop + target.clientHeight >= target.scrollHeight) {
      this.currentPage += 1;

      if (selectName === 'explanationContentSelect') {
        this.store.dispatch(
          new ExplanationContentActions.FetchAppend({
            pageNumber: this.currentPage,
            pageSize: 10,
            skipStoreDispatch: true,
          }),
        );
      }

      if (selectName === 'explanationSelect') {
        this.store.dispatch(
          new ExplanationActions.FetchAppend({
            pageNumber: this.currentPage,
            pageSize: 10,
            skipStoreDispatch: true,
          }),
        );
      }

      if (selectName === 'sequenceSelect') {
        this.store.dispatch(
          new SequenceActions.FetchAppend({
            pageNumber: this.currentPage,
            pageSize: 10,
            skipStoreDispatch: true,
          }),
        );
      }

      if (selectName === 'testSelect') {
        this.store.dispatch(
          new TestActions.FetchAppend({
            pageNumber: this.currentPage,
            pageSize: 10,
            skipStoreDispatch: true,
          }),
        );
      }
    }
  }

  onEditorSelectClosed() {
    if (this.selectedContent instanceof ExplanationContentJsonModel) {
      this.store.dispatch(
        new ExplanationContentActions.FetchOne(this.selectedContent.id),
      );
      this.explanationContentName.setValue(this.selectedContent.name);
      this.explanationContentHtml = this.selectedContent.content;
    }
  }

  setEditorContent(): void {
    if (this.data.content instanceof ExplanationContentJsonModel) {
      this.selectedContent = this.data.content;
      this.explanationContentName.setValue(this.data.content.name);
      this.explanationContentHtml = toggleReadOnlyMathEditorInput(
        this.data.content.content,
        'remove',
      ); // remove readonly flag from <mateditor-input />
      this.explanationContentHtml = toggleContenteditable(
        this.explanationContentHtml,
        'set',
      ); // set contenteditable in box to true
    }
  }

  checkNewEditorContent(): boolean {
    return !(
      this.selectedContent instanceof ExplanationContentJsonModel ||
      this.data.content instanceof ExplanationContentJsonModel
    );
  }

  createNewEditorContent(): Observable<ExplanationContentJsonModel | null> {
    const newExplanationContent = new ExplanationContentJsonModel();
    newExplanationContent.name = this.explanationContentName.value as string;
    newExplanationContent.content = this.explanationContentHtml;

    return this.store
      .dispatch(new ExplanationContentActions.AddRaw(newExplanationContent))
      .pipe(
        switchMap((result) => {
          const createdContent =
            result as unknown as ExplanationContentJsonModel;

          return this.store
            .select(ExplanationContentState.getById(createdContent.id))
            .pipe(
              filter((content) => !!content),
              take(1),
              map(() => {
                this.explanationContentName.setErrors(null);
                this.explanationContentName.markAsUntouched();
                this.explanationContentName.updateValueAndValidity();
                return createdContent;
              }),
            );
        }),
        catchError((error) => {
          const errors = error?.error?.errors;
          if (errors?.length) {
            for (const err of errors) {
              if (
                err.source?.pointer === '/data/attributes/name' &&
                err.status === '422'
              ) {
                this.explanationContentName.setErrors({ unique: true });
                this.explanationContentName.markAsTouched();
              }
            }
          }

          return of(null); // prevent crash
        }),
      );
  }

  updateEditorContent(): Observable<ExplanationContentJsonModel | null> {
    return this.explanationContent$.pipe(
      take(1),
      switchMap((content) => {
        if (!content) {
          return of(null);
        }

        content.name = this.explanationContentName.value as string;
        content.content = this.copyAndSaveExplanationContentHtml;

        return this.store
          .dispatch(new ExplanationContentActions.EditRaw(content))
          .pipe(
            map(() => {
              this.explanationContentName.setErrors(null);
              this.explanationContentName.updateValueAndValidity();
              return content;
            }),
            catchError((error) => {
              const errors = error?.error?.errors;
              if (errors?.length) {
                for (const err of errors) {
                  if (
                    err.source?.pointer === '/data/attributes/name' &&
                    err.detail === 'validation.unique'
                  ) {
                    this.explanationContentName.setErrors({ unique: true });
                    this.explanationContentName.markAsTouched();
                  }
                }
              }
              return of(null);
            }),
          );
      }),
    );
  }

  public async onCopy(): Promise<void> {
    const originalName = this.explanationContentName.value?.trim() ?? '';
    const copyName = originalName + ' (Kopie)';

    this.explanationContentName.setValue(copyName);

    // Start creation
    await firstValueFrom(this.createNewEditorContent());

    // Now wait for it to actually appear in the store
    const created = await firstValueFrom(
      this.store.select(ExplanationContentState.getExplanationContent).pipe(
        filter((c) => !!c && c.name === copyName),
        take(1),
      ),
    );

    if (created) {
      this.selectedContent = created;
      this.snackBar.open('Kopie erstellt.', 'Schließen', { duration: 3000 });
    }
    else {
      this.snackBar.open('Fehler beim Erstellen der Kopie.', 'Schließen', {
        duration: 5000,
      });
    }
  }

  async onSave(): Promise<void> {
    if (this.formGroup.valid) {
      this.store.dispatch([
        new FetchOptionsActions.UpdateSortFieldName(''),
        new FetchOptionsActions.UpdateFilters([]),
      ]);

      const formData = this.formGroup.value;

      if (this.selectedTabLabel === 'editor') {
        if (
          !this.explanationContentHtml ||
          this.explanationContentHtml.trim() === ''
        ) {
          this.explanationContentHtml = '<p></p>';
        }
        else {
          this.copyAndSaveExplanationContentHtml =
            toggleReadOnlyMathEditorInput(this.explanationContentHtml, 'set'); // add readonly flag from <mateditor-input />
          this.copyAndSaveExplanationContentHtml = toggleContenteditable(
            this.copyAndSaveExplanationContentHtml,
            'remove',
          ); // set contenteditable in box to false
        }

        if (this.checkNewEditorContent()) {
          const created = await firstValueFrom(this.createNewEditorContent());

          if (!created || this.explanationContentName.invalid) {
            return;
          }

          const updatedContent = await firstValueFrom(
            this.explanationContent$.pipe(take(1)),
          );
          this.selectedContent = updatedContent;

          const container = await firstValueFrom(
            this.store
              .select(LearningSectionContainerState.getLearningSectionContainer)
              .pipe(take(1)),
          );

          if (container && updatedContent) {
            container.content = updatedContent;
            await firstValueFrom(
              this.store.dispatch(
                new LearningSectionContainerActions.EditRaw(container),
              ),
            );
          }
        }
        else {
          const updated = await firstValueFrom(this.updateEditorContent());

          if (!updated || this.explanationContentName.invalid) {
            return;
          }

          this.selectedContent = updated;
        }
      }

      if (this.selectedContent) {
        formData.content = this.selectedContent;
      }

      this.data.update(formData);

      try {
        await firstValueFrom(
          this.store.dispatch(
            new LearningSectionContainerActions.Update(this.data),
          ),
        );
        this.snackBar.open('Speicherung erfolgreich', 'Schließen', {
          duration: 3000,
          verticalPosition: 'bottom',
          panelClass: ['snackbar-success'],
        });
        this.setActiveTab();
      }
      catch {
        this.snackBar.open(
          'Bei der Speicherung ist ein Fehler aufgetreten',
          'Schließen',
          {
            duration: 3000,
            verticalPosition: 'bottom',
            panelClass: ['snackbar-error'],
          },
        );
      }
    }
  }

  public checkForErrors(formGroup: AbstractControl): string {
    if (formGroup.hasError('required')) {
      return 'Eingabe erforderlich';
    }

    return '';
  }

  onCancel(): void {
    this.store.dispatch([
      new FetchOptionsActions.UpdateSortFieldName(''),
      new FetchOptionsActions.UpdateFilters([]),
    ]);

    this.dialogRef.close();
  }

  onClose(): void {
    this.dialogRef.close();
  }

  protected readonly getLearningSectionContainerContentType =
    getLearningSectionContainerContentType;
}
